import { RFQ } from "../../../types/rfq";
import { TeamMember } from "../../../types";

// The quote email's plain-text body and its HTML rendering. Both were inline
// in EmailPreviewDialog; they are pure, so they live here where tests import
// them directly.

// Generate default email body
export const generateDefaultEmailBody = (
  rfq: RFQ,
  user: TeamMember | null,
  customSignoff?: string | null
): string => {
  const userName = user?.name || "DBR Systems";  // Use company name as fallback
  const userMobile = user?.mobile || "";
  
  // Create company signoff with user details - in plain text
  const signoff = customSignoff || `${userName}\n` +
    `DBR SYSTEMS PTE LTD\n` +
    `1 ROCHOR CANAL ROAD\n` +
    `#02-30 SIM LIM SQUARE\n` +
    `SINGAPORE 188504\n` +
    `TEL: 63347055 FAX: 63365930${userMobile ? `\nHP: ${userMobile}` : ''}`;
  
  // Build the item details list based on validated items
  const validatedItems = (rfq.items || [])
    .filter(item => item.price_validated)
    .slice()
    .sort((a, b) => (a.order_index ?? a.rfq_item_id) - (b.order_index ?? b.rfq_item_id));
  let itemDetailsList = "";
  if (validatedItems.length > 0) {
    itemDetailsList = "";  // Removed the header text
    validatedItems.forEach((item, index) => {
      const itemName = item.item_name || 'Unknown Item';
      const customRemarks = item.remarks || '';

      // Show "Item not available" for items with zero price
      const numericPrice = Number(item.quoted_price);
      if (item.quoted_price == null || isNaN(numericPrice)) {
        console.warn(`[DATA WARNING] Item "${itemName}" (ID: ${item.rfq_item_id}) has price_validated=true but quoted_price=${item.quoted_price}`);
      }
      const quotedPrice = numericPrice === 0 ? 'Item not available' : !isNaN(numericPrice) && item.quoted_price != null ? `$${numericPrice.toFixed(2)}` : 'Price pending';

      // Two-line format (client request - Fuji): the requested item on the
      // numbered line, then what is actually quoted on its own 'Quote:'
      // line - the price directly after what it prices, flags last - then
      // a blank line before the next item. The requested item is not
      // repeated on the Quote line; it is on the numbered line above.
      const flags = [
        item.indent_days && item.indent_days > 0 ? `${item.indent_days} ${item.indent_days === 1 ? 'Day' : 'Days'} indent` : null,
        item.is_limited_stock ? 'limited stock' : null,
        item.is_non_refundable ? 'non-returnable' : null
      ].filter(part => part);

      // 'offer Epson L5590 ... 220v using Epson 003 black $11 / ...' names
      // the offered product before 'using <consumables>' - the quoted
      // price belongs to that product, so insert it between the two rather
      // than after the consumable prices. Only when that same line carries
      // prices - that confusion is the whole reason to insert - so a
      // validation remark ('kindly validate if currently is using HP
      // 215A') or a multi-line options quote (prices on later lines) keeps
      // the price after the remark instead of being spliced mid-sentence.
      // Function replacement, never a '$'-bearing replacement string: a
      // '$19.00' price would have its '$1' read as a capture-group
      // reference.
      const usingMatch = customRemarks ? customRemarks.match(/(\s+)(using)(\s+)/i) : null;
      const priceInsertsIntoRemark = usingMatch !== null
        && quotedPrice.startsWith('$')
        // A non-global String.match always sets index
        && /\$\d/.test(customRemarks.slice(usingMatch.index! + usingMatch[0].length).split('\n')[0]);
      const quotedRemark = priceInsertsIntoRemark
        ? customRemarks.replace(/(\s+)(using)(\s+)/i, (_match, lead, usingWord, trail) => `${lead}- ${quotedPrice} - ${usingWord}${trail}`)
        : [customRemarks || null, quotedPrice].filter(part => part).join(' - ');
      const quoteParts = [quotedRemark, ...flags].filter(part => part);
      itemDetailsList += `${index + 1}. ${itemName}\nQuote: ${quoteParts.join(' - ')}\n\n`;
    });
  } else {
    itemDetailsList = "We are currently processing your item details.\n\n";
  }

  const emailBody = `${itemDetailsList}` +
    `${signoff}`;  // Removed the 30-day validity text

  return emailBody;
};

// Convert plain text to HTML for sending
export const convertToHtml = (text: string): string => {
  const escapeHtml = (unsafe: string) =>
    unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  // Define common styles as objects for inline use
  const styles = {
    emailContent: 'font-family: Arial, sans-serif; line-height: 1.6; color: #333; padding: 20px;',
    paragraph: 'margin: 0 0 1em 0;',
    item: 'margin: 0 0 4px 0;',
    itemBlock: 'padding: 0 0 16px 0; margin: 0;',
    remark: 'margin: 0; font-weight: bold;',
    addressBlock: 'margin: 0.5em 0;',
    addressLine: 'margin: 0; line-height: 1.2;'
  };
  
  // Split into paragraphs first (double newlines)
  const paragraphs = text.split('\n\n').map(para => {
    // Split paragraph into lines
    const lines = para.split('\n');
    
    // Process each line in the paragraph
    const processedLines = lines.map(line => {
      const trimmedLine = line.trim();
      
      if (!trimmedLine) return '';
      
      const escaped = escapeHtml(trimmedLine);
      
      // Check if this is an item line (starts with a number followed by a period)
      if (trimmedLine.match(/^\d+\./)) {
        return `<div data-dbr-item style="${styles.item}">${escaped}</div>`;
      }
      
      // Quote line for an item - separate line, bold
      if (trimmedLine.match(/^Quote:/i)) {
        return `<div data-dbr-quote style="${styles.remark}">${escaped}</div>`;
      }
      
      // Remarks line for an item - separate line, bold
      if (trimmedLine.match(/^Remarks:/i)) {
        return `<div data-dbr-remark style="${styles.remark}">${escaped}</div>`;
      }
      
      // Check if this is part of the address block
      if (trimmedLine === 'DBR SYSTEMS PTE LTD' || 
          trimmedLine.match(/^(1 ROCHOR|#02-30|SINGAPORE|TEL:|HP:)/)) {
        return `<div style="${styles.addressLine}">${escaped}</div>`;
      }
      
      // Regular line within a paragraph
      return escaped;
    });
    
    // Join lines in a paragraph
    const processedPara = processedLines.filter(line => line).join('');
    
    // Wrap in paragraph div if it's not a special block (address or item)
    if (!processedPara.includes('style="')) {
      return `<div style="${styles.paragraph}">${processedPara}</div>`;
    }
    
    // If this is an address block, wrap it in an address block div
    if (processedPara.includes(styles.addressLine)) {
      return `<div style="${styles.addressBlock}">${processedPara}</div>`;
    }
    
    // Item block (numbered item plus its Quote line). The bottom padding
    // renders the blank line after each line item - padding, not margin,
    // because desktop Outlook drops div margins.
    if (processedPara.includes('data-dbr-item')) {
      return `<div style="${styles.itemBlock}">${processedPara}</div>`;
    }
    
    return processedPara;
  });
  
  // Join all paragraphs
  const formattedText = paragraphs.filter(para => para).join('\n');

  // Return complete HTML document structure
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body>
<div style="${styles.emailContent}">
${formattedText}
</div>
</body>
</html>`;
};
